/**
 * The local scoreboard: the ten best runs of each timed course, kept in the
 * browser's localStorage, plus the player's name. A board belongs to a course
 * layout, not just a course name: the key carries a hash of the geometry and
 * the lines, so editing a course starts a fresh board and old times are never
 * compared against a different course. Storage can be missing or full (a
 * private window, a thumbnail capture); every read and write survives that.
 */

import {
  cleanPlayerName,
  DEFAULT_NAME,
  isExplicitName,
  MAX_NAME_LENGTH,
  normalizePlayerName,
} from "@lache/relay/names";
import type { CourseData } from "@lache/sim";

export { DEFAULT_NAME, MAX_NAME_LENGTH };

export interface ScoreEntry {
  name: string;
  /** Endurance boards rank completed obstacles before time. Absent on timed courses. */
  obstacles?: number;
  duration?: number;
  /** Run time, s. */
  time: number;
  /** Falls into water during the run. */
  falls: number;
  /** When the run was set, epoch ms. */
  date: number;
  /** Global scores may also retain the client's finish date, independently of server receipt order. */
  achievedAt?: number;
  /** Tuning hash the run was set with. */
  tuning: string;
  /** Split times at the checkpoints, s, by respawn index (0 is the start line). */
  splits: number[];
}

export interface Placement {
  entry: ScoreEntry;
  /** 1-based place on the board, or null when the run did not make the top ten. */
  rank: number | null;
  /** The board after the run. */
  board: ScoreEntry[];
  /** The player's best time on this board before the run, or null. */
  previousBest: number | null;
  /** The course record before the run, or null. */
  previousRecord: number | null;
}

export const BOARD_SIZE = 10;
const SCORES_KEY = "lache.scores.v1";
const NAME_KEY = "lache.player.v1";
/** This new prompt must be completed once even when an earlier release already saved a name. */
const NAME_CONFIRMED_KEY = "lache.player.confirmed.v1";

/** The board key of a course: its name and a hash of everything that decides a time. */
export function courseKey(course: CourseData): string {
  const shape = JSON.stringify({
    start: course.start,
    startLine: course.startLine,
    finishLine: course.finishLine,
    respawns: course.respawns.map((r) => [r.x, r.y]),
    entities: course.entities,
  });
  let h = 0x811c9dc5;
  for (let i = 0; i < shape.length; i++) {
    h ^= shape.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `${course.name}@${h.toString(16).padStart(8, "0")}`;
}

/** Faster first; an equal time keeps the earlier run ahead. */
function byTime(a: ScoreEntry, b: ScoreEntry): number {
  return a.time - b.time || a.date - b.date;
}

export function cleanName(name: string): string {
  return cleanPlayerName(name);
}

export class ScoreStore {
  private boards: Record<string, ScoreEntry[]> = {};
  private selectedName: string | null = null;
  private nameConfirmed = false;

  constructor(private readonly storage: Storage | null = safeLocalStorage()) {
    this.boards = this.load();
    try {
      const savedName = this.storage?.getItem(NAME_KEY);
      if (savedName && isExplicitName(savedName)) {
        // Older versions accepted any name. Ask for a replacement without touching run history.
        this.storage?.removeItem(NAME_KEY);
        this.storage?.removeItem(NAME_CONFIRMED_KEY);
      } else {
        if (savedName && normalizePlayerName(savedName)) this.selectedName = cleanName(savedName);
        this.nameConfirmed = this.storage?.getItem(NAME_CONFIRMED_KEY) === "1";
      }
    } catch {
      // Storage unavailable: a name can still be chosen for this session.
    }
  }

  private load(): Record<string, ScoreEntry[]> {
    try {
      const raw = this.storage?.getItem(SCORES_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (typeof parsed !== "object" || parsed === null) return {};
      const out: Record<string, ScoreEntry[]> = {};
      for (const [key, list] of Object.entries(parsed as Record<string, unknown>)) {
        if (!Array.isArray(list)) continue;
        out[key] = list
          .filter(isEntry)
          .map((entry) => ({ ...entry, name: cleanName(entry.name) }))
          .sort(byTime)
          .slice(0, BOARD_SIZE);
      }
      return out;
    } catch {
      return {};
    }
  }

  /** Merge the latest shared boards before writing; another tab may have finished a run. */
  private syncBoards(): void {
    const shared = this.load();
    for (const [key, local] of Object.entries(this.boards)) {
      const entries = shared[key] ?? [];
      // Keep duplicate finishes already present on a board, without multiplying them each save.
      const signature = (entry: ScoreEntry): string =>
        JSON.stringify([
          entry.name,
          entry.time,
          entry.falls,
          entry.date,
          entry.tuning,
          entry.splits,
          entry.achievedAt,
          entry.obstacles,
          entry.duration,
        ]);
      const stored = new Map<string, number>();
      for (const entry of entries) {
        const id = signature(entry);
        stored.set(id, (stored.get(id) ?? 0) + 1);
      }
      const seen = new Map<string, number>();
      for (const entry of local) {
        const id = signature(entry);
        const count = (seen.get(id) ?? 0) + 1;
        seen.set(id, count);
        if (count > (stored.get(id) ?? 0)) entries.push(entry);
      }
      shared[key] = entries.sort(byTime).slice(0, BOARD_SIZE);
    }
    this.boards = shared;
  }

  private save(): void {
    try {
      this.storage?.setItem(SCORES_KEY, JSON.stringify(this.boards));
    } catch {
      // Storage unavailable or full: the board still works for this session.
    }
  }

  board(key: string): ScoreEntry[] {
    return this.boards[key] ?? [];
  }

  /** The course record, or null. */
  record(key: string): ScoreEntry | null {
    return this.board(key)[0] ?? null;
  }

  /** The best entry set under `name`, or null. */
  personalBest(key: string, name: string): ScoreEntry | null {
    return this.board(key).find((e) => e.name === name) ?? null;
  }

  /** Record a finished run and return where it placed. */
  add(key: string, entry: ScoreEntry): Placement {
    this.syncBoards();
    entry = { ...entry, name: cleanName(entry.name) };
    const before = this.board(key);
    const previousBest = this.personalBest(key, entry.name)?.time ?? null;
    const previousRecord = before[0]?.time ?? null;
    const board = [...before, entry].sort(byTime).slice(0, BOARD_SIZE);
    const index = board.indexOf(entry);
    this.boards[key] = board;
    this.save();
    return { entry, rank: index >= 0 ? index + 1 : null, board, previousBest, previousRecord };
  }

  /** A name was saved before this load or explicitly chosen during this session. */
  get hasName(): boolean {
    return this.selectedName !== null;
  }

  /** A saved name alone does not count as answering the current welcome prompt. */
  get needsNamePrompt(): boolean {
    return !this.hasName || !this.nameConfirmed;
  }

  confirmName(value: string): void {
    this.selectedName = cleanName(value);
    this.nameConfirmed = true;
    try {
      this.storage?.setItem(NAME_KEY, this.selectedName);
      this.storage?.setItem(NAME_CONFIRMED_KEY, "1");
    } catch {
      // Keep the answer this session; a later load asks again if it could not be saved.
    }
  }

  get name(): string {
    return this.selectedName ?? DEFAULT_NAME;
  }

  set name(value: string) {
    this.selectedName = cleanName(value);
    try {
      this.storage?.setItem(NAME_KEY, this.selectedName);
    } catch {
      // Storage unavailable: the name lasts until reload.
    }
  }
}

function isEntry(v: unknown): v is ScoreEntry {
  if (typeof v !== "object" || v === null) return false;
  const e = v as Record<string, unknown>;
  if (typeof e.name !== "string" || typeof e.time !== "number" || !Number.isFinite(e.time)) return false;
  if (typeof e.falls !== "number" || typeof e.date !== "number") return false;
  if (typeof e.tuning !== "string") e.tuning = "";
  if (!Array.isArray(e.splits)) e.splits = [];
  return true;
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

/** A run time as m:ss.mmm. */
export function formatTime(seconds: number): string {
  const ms = Math.max(0, Math.round(seconds * 1000));
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const frac = ms % 1000;
  return `${m}:${String(s).padStart(2, "0")}.${String(frac).padStart(3, "0")}`;
}

/** A signed difference in seconds, like +0.532 or -1.204 (with a true minus sign). */
export function formatDelta(seconds: number): string {
  const ms = Math.round(Math.abs(seconds) * 1000);
  const sign = seconds < 0 && ms > 0 ? "−" : "+";
  return `${sign}${Math.floor(ms / 1000)}.${String(ms % 1000).padStart(3, "0")}`;
}
